/**
 * Pick the default payment-method tile to pre-select when the checkout
 * first renders. The server already orders `methodEligibility` cheapest-first
 * (primary tier first, then displayOrder), so the right choice is just
 * "the first enabled, non-hidden tile in the array". Returns null when
 * nothing qualifies — the caller is responsible for keeping the
 * Verify-and-continue button disabled in that case.
 */
import type { CheckoutConfigDto } from './api.js';

type Tile = CheckoutConfigDto['strategy']['methodEligibility'][number];

export function pickDefaultMethod(methods: Tile[]): string | null {
  // Filter, then rank by tier (primary < secondary), then by the operator-
  // authored displayOrder. The server already returns tiles in this order,
  // but ranking here keeps the helper correct even when called with an
  // unsorted array (defence-in-depth, makes the unit-tested contract
  // independent of the API's sort behaviour).
  const eligible = methods.filter((m) => m.enabled && m.tier !== 'hidden');
  if (eligible.length === 0) return null;
  const tierRank = (t: Tile['tier']) => (t === 'primary' ? 0 : t === 'secondary' ? 1 : 0);
  const sorted = [...eligible].sort((a, b) => {
    const tr = tierRank(a.tier) - tierRank(b.tier);
    if (tr !== 0) return tr;
    return (a.displayOrder ?? 99) - (b.displayOrder ?? 99);
  });
  return sorted[0]?.paymentMethod ?? null;
}
